import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, AppBar, Typography, IconButton, Avatar, Menu, MenuItem,
  Divider, useTheme, useMediaQuery,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CategoryIcon from '@mui/icons-material/Category';
import MovieIcon from '@mui/icons-material/Movie';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../contexts/AuthContext';

const DRAWER_WIDTH = 240;

const NAV_ITEMS = [
  { label: 'Users',      path: '/users',      icon: <PeopleAltIcon />,           accessKey: 'users' },
  { label: 'Categories', path: '/categories', icon: <CategoryIcon />,            accessKey: 'categories' },
  { label: 'Shows',      path: '/shows',      icon: <MovieIcon />,               accessKey: 'shows' },
  { label: 'User Logs',  path: '/userlogs',   icon: <AssignmentIcon />,          accessKey: 'userLogs' },
  { label: 'Analytics',  path: '/analytics',  icon: <QueryStatsIcon />,          superAdminOnly: true },
  { label: 'Admins',     path: '/admins',     icon: <AdminPanelSettingsIcon />,  accessKey: 'admins' },
];

const hasAccess = (item, user) => {
  // Analytics isn't part of the configurable adminAccess grid — it's superadmin-only, always.
  if (item.superAdminOnly) return user?.userType === 'superadmin';
  if (!item.accessKey) return true;
  const access = user?.adminAccess?.[item.accessKey];
  if (!access) return true; // no restrictions = full access
  return access.read || access.write;
};

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0f0f13' }}>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', letterSpacing: '-0.5px' }}>
          JAMSAP Admin
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      <List sx={{ flex: 1, px: 1.5, pt: 2 }}>
        {NAV_ITEMS.filter((item) => hasAccess(item, user)).map(({ label, path, icon }) => {
          const active = location.pathname.startsWith(path);
          return (
            <ListItemButton
              key={path}
              onClick={() => { navigate(path); if (isMobile) setMobileOpen(false); }}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                color: active ? '#fff' : 'rgba(255,255,255,0.5)',
                bgcolor: active ? 'rgb(201 201 201 / 20%)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' },
                transition: 'all 0.15s',
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{icon}</ListItemIcon>
              <ListItemText
                primary={label}
                primaryTypographyProps={{ fontWeight: active ? 600 : 400, fontSize: 14 }}
              />
              {active && (
                <Box sx={{ width: 3, height: 20, bgcolor: '#fec958', borderRadius: 4 }} />
              )}
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', textAlign: 'center' }}>
          JAMSAP TV Admin
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8f9fc' }}>
      {/* Sidebar – desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none' },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Sidebar – mobile */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{ bgcolor: '#fff', borderBottom: '1px solid #e8eaf0', color: 'text.primary' }}
        >
          <Toolbar>
            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flex: 1 }} />
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#fec958', fontSize: 14 }}>
                {user?.name?.[0] || user?.email?.[0] || 'A'}
              </Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {user?.email || 'Admin'}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { setAnchorEl(null); logout(); }}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
